/**
 * Work → All work — prototype `work` (docs/prototype.html 2758, managers' override 4319): one list of
 * everything open (tasks, requests, escalations, approvals), GET /work. The server decides what the caller
 * sees (a member: their own work and their own clients'); the "Whose work" pills show for anyone who can read
 * all tasks.
 *
 * Status, type, person and client filter on the server (GET /work pages them); grouping and the pill counts
 * work on the loaded rows, so a count reads "12+" while more pages exist. Filters live in the address so Back and reload keep them; `?task=` opens the side panel.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { INTERNAL_REQUEST_STATUS_LABEL, MAX_ASSIGNEE_FILTER, INTERNAL_REQUEST_TYPE_LABEL, TASK_SOURCE_LABEL, type ListWorkQuery, type Priority, type WorkItem, type WorkKind, type WorkStatusFilter } from '@amplispot/contracts';
import { useClients } from '../../lib/data';
import { dayLabel, firstName, todayIso } from '../../lib/format';
import { useSession } from '../../lib/session';
import { daysBetween, dueText, useWhose, useWorkList } from '../../lib/work';
import { Avatar, Empty, ErrorState, Loading, MultiSelectFilter, Pills, PriorityChip } from '../../ui/kit';
import type { GetApiV1WorkQuery } from '../../../../../packages/contracts/api';
import { AddChooser } from '../requests/ItemModals';
import { ApprovalsBoard } from './ApprovalsBoard';
import { HistoryImportModal } from './HistoryImport';
import { clientTaskChip } from '../requests/ItemPanels';
import { SEVERITY, rowClock, shortDay } from '../../lib/requests';

type TypeKey = 'all' | 'task' | 'request' | 'escalation' | 'approval';
type GroupKey = 'client' | 'who' | 'due' | 'type';

const TYPE_OF: Record<WorkKind, Exclude<TypeKey, 'all'> | null> = {
  task: 'task',
  client_task: 'task',
  client_request: 'request',
  internal_request: 'request',
  escalation: 'escalation',
  approval: 'approval',
  proposed: null,
};

/** The type pills as GET /work `?type` values (a client task counts as a task, both request kinds as requests). */
const TYPE_QUERY: Record<Exclude<TypeKey, 'all'>, string> = { task: 'task,client_task', request: 'request', escalation: 'escalation', approval: 'approval' };

const KIND_LABEL: Record<WorkKind, string> = {
  task: 'Task',
  client_task: 'Task',
  client_request: 'Request',
  internal_request: 'Request',
  escalation: 'Escalation',
  approval: 'Approval',
  proposed: 'Proposed',
};

const STATUS: [WorkStatusFilter, string][] = [
  ['open', 'Open'],
  ['overdue', 'Overdue'],
  ['blocked', 'Blocked'],
  ['proof', 'Proof to check'],
  ['proposed', 'Proposed'],
  ['done', 'Done'],
  ['all', 'All'],
];

const PRIO_RANK: Record<Priority, number> = { high: 0, med: 1, low: 2 };

export function AllWork() {
  const { can } = useSession();
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const manager = can('tasks.read.all');
  // ?who is a comma list of user ids and/or 'none' (Not assigned); absent (or the old 'all') = everyone. GET /work takes the same list.
  const who = (sp.get('who') ?? '').split(',').filter((x) => x && x !== 'all');
  const type = (sp.get('type') ?? 'all') as TypeKey;
  const status = (sp.get('status') ?? 'open') as WorkStatusFilter;
  const client = sp.get('client') ?? 'all';
  const group = (sp.get('group') ?? 'client') as GroupKey;
  // ?kind=client_task narrows the Tasks pill to promises made to a client (GET /work type=client_task) — what Today's
  // "Client tasks overdue" counts, so the number and the list it opens agree.
  const clientOnly = type === 'task' && sp.get('kind') === 'client_task';
  // Prototype `boardOn`: Approvals can show as the document board by stage (VIEWS.approvals, in hub mode).
  const boardOn = type === 'approval' && sp.get('board') === '1';

  // Prototype `setWF`: changing a filter goes back to the list (WF.board=false) — whose work, status and grouping
  // shape the list, not the board. The client filter is the one the board follows, so it stays on the board.
  const set = (k: string, v: string, dflt: string) => {
    const n = new URLSearchParams(sp);
    if (v === dflt) n.delete(k);
    else n.set(k, v);
    if (k !== 'board' && k !== 'client') n.delete('board');
    if (k === 'type') n.delete('kind');
    setSp(n, { replace: true });
  };

  // Typed by both the route schema and the generated contract (packages/contracts/api.ts, GET /api/v1/work v2).
  const query: ListWorkQuery & GetApiV1WorkQuery = {
    status,
    ...(type !== 'all' ? { type: clientOnly ? 'client_task' : TYPE_QUERY[type] } : {}),
    ...(manager && who.length ? { assignee_id: who.join(',') } : {}),
    ...(client !== 'all' ? { client_id: client } : {}),
    // Page by the grouping, so a page holds whole groups and only the one it ends in is cut (cutGroups below).
    // By due date needs none: the server's default order (overdue, then due date) already runs bucket by bucket.
    ...(group !== 'due' ? { group_by: group } : {}),
  };
  const list = useWorkList(query);
  const whose = useWhose(manager, client !== 'all' ? client : undefined);
  const clients = useClients();

  const items = list.items;
  const more = list.more;
  const counts = useMemo(() => {
    const c: Record<TypeKey, number> = { all: 0, task: 0, request: 0, escalation: 0, approval: 0 };
    for (const i of items) {
      const t = TYPE_OF[i.kind];
      c.all++;
      if (t) c[t]++;
    }
    return c;
  }, [items]);
  const shown = type === 'all' ? items : items.filter((i) => TYPE_OF[i.kind] === type && (!clientOnly || i.kind === 'client_task'));
  const groups = useMemo(() => groupRows(shown, group), [shown, group]);
  const cut = useMemo(() => cutGroups(items[items.length - 1], group, more), [items, group, more]);

  // A row opens its own route. Rows routed back to this list open the panel and keep the filters.
  const openItem = (i: WorkItem) => {
    const u = new URL(i.route, window.location.origin);
    if (u.pathname === '/work/allwork') {
      const n = new URLSearchParams(sp);
      u.searchParams.forEach((v, k) => n.set(k, v));
      setSp(n);
    } else nav(i.route);
  };

  const plus = more ? '+' : '';
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, margin: '0 0 10px' }}>
        {/* Prototype work toolbar: managers on the Tasks or Escalations pill get the importer left of + Add. */}
        {can('task.reassign') && (type === 'task' || type === 'escalation') ? (
          <button className="btn" onClick={() => setImporting(true)}>
            Import old {type === 'task' ? 'tasks' : 'escalations'}
          </button>
        ) : null}
        {can('tasks.read.own') || manager ? (
          <button className="btn pri" onClick={() => setAdding(true)}>
            + Add
          </button>
        ) : null}
      </div>

      {manager ? (
        <div className="bar" style={{ marginBottom: 8 }}>
          {/* GET /work assignee_id takes a comma list of up to 50 (t-694b4e93), hence max. */}
          <MultiSelectFilter
            label="Whose work"
            max={MAX_ASSIGNEE_FILTER}
            allLabel={`Everyone${whose.data ? ` · ${whose.data.everyone.open}` : ''}`}
            options={[
              ...(whose.data?.data ?? []).map((r) => ({ value: r.user.id, label: r.user.name, count: r.overdue ? `${r.open} · ${r.overdue} overdue` : r.open })),
              { value: 'none', label: 'Not assigned', count: whose.data?.unassigned.open },
            ]}
            value={who}
            onChange={(v) => set('who', v.join(','), '')}
          />
        </div>
      ) : null}

      <div className="bar" style={{ marginBottom: 8 }}>
        <Pills<TypeKey>
          options={[
            ['all', 'Everything', counts.all],
            ['task', 'Tasks', counts.task],
            ['request', 'Requests', counts.request],
            ['escalation', 'Escalations', counts.escalation],
            ['approval', 'Approvals', counts.approval],
          ].map(([k, l, n]) => [k as TypeKey, l as string, type === 'all' || k === type ? `${n}${plus}` : undefined])}
          value={type}
          onChange={(v) => set('type', v, 'all')}
        />
        {clientOnly ? (
          <button className="fp on" onClick={() => set('kind', 'all', 'all')} aria-label="Show all tasks, not only client tasks">
            Client tasks only ×
          </button>
        ) : null}
      </div>
      <div className="bar" style={{ marginBottom: 12 }}>
        <Pills<WorkStatusFilter> options={STATUS} value={status} onChange={(v) => set('status', v, 'open')} />
        <span className="grow" />
        <select className="in" style={{ width: 'auto', padding: '5px 8px' }} value={client} onChange={(e) => set('client', e.target.value, 'all')} aria-label="Client">
          <option value="all">All clients</option>
          {(clients.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select className="in" style={{ width: 'auto', padding: '5px 8px' }} value={group} onChange={(e) => set('group', e.target.value, 'client')} aria-label="Group by">
          <option value="client">By client</option>
          <option value="who">By person</option>
          <option value="due">By due date</option>
          <option value="type">By type</option>
        </select>
      </div>

      {type === 'approval' ? (
        boardOn ? (
          <>
            <div className="note" style={{ marginBottom: 12 }}>
              Approvals by stage.{' '}
              <a href="#" onClick={(e) => (e.preventDefault(), set('board', '0', '0'))} style={{ color: 'var(--brand)', fontWeight: 600 }}>
                Back to the list
              </a>
            </div>
            <ApprovalsBoard clientId={client !== 'all' ? client : undefined} />
          </>
        ) : (
          <div style={{ margin: '-4px 0 10px' }}>
            <button className="btn sm" onClick={() => set('board', '1', '0')}>
              Show as a board by stage
            </button>
          </div>
        )
      ) : null}

      {boardOn ? null : list.isLoading ? (
        <div className="panel">
          <Loading />
        </div>
      ) : list.error ? (
        <div className="panel">
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        </div>
      ) : !groups.length && !more ? (
        <div className="panel">
          <Empty title="Nothing here">Switch the filter above, or add a task.</Empty>
        </div>
      ) : (
        groups.map((g) => (
          <div className="panel" style={{ marginBottom: 14 }} key={g.key}>
            <div className="hd">
              <h3>
                {g.label} {g.prio ? <PriorityChip p={g.prio} /> : null}
              </h3>
              <div className="r">
                <span className="sub">
                  {g.rows.length}
                  {cut.has(g.key) ? '+' : ''}
                </span>
              </div>
            </div>
            {g.rows.map((i) => (
              <WorkRow key={`${i.kind}:${i.id}`} item={i} onOpen={() => openItem(i)} />
            ))}
          </div>
        ))
      )}
      {!boardOn && more ? (
        <div style={{ textAlign: 'center', margin: '4px 0 18px' }}>
          <button className="btn" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage} aria-busy={list.isFetchingNextPage}>
            {list.isFetchingNextPage ? 'Loading…' : 'Show more'}
          </button>
        </div>
      ) : null}
      {adding ? <AddChooser onClose={() => setAdding(false)} clientId={client !== 'all' ? client : undefined} /> : null}
      {importing ? <HistoryImportModal onClose={() => setImporting(false)} /> : null}
    </>
  );
}

// ---------------------------------------------------------------- one row

/** The person column: who is responsible, and in what capacity (SPOC, covering for the SPOC, owner). */
function Own({ item }: { item: WorkItem }) {
  if (!item.assignee) return <span className="chip cold" style={{ width: 150, justifyContent: 'center', flex: 'none' }}>Not assigned</span>;
  const spoc = item.client?.spoc ?? null;
  const cap = !item.client ? 'Owner' : spoc && spoc.id === item.assignee.id ? 'SPOC' : `Covering for ${spoc ? firstName(spoc.name) : 'the SPOC'}`;
  return (
    <span className="own" title={`Responsible: ${item.assignee.name}`}>
      <Avatar user={item.assignee} size={26} />
      <span>
        <b>{firstName(item.assignee.name)}</b>
        <small>{cap}</small>
      </span>
    </span>
  );
}

function subLine(i: WorkItem): string {
  switch (i.kind) {
    case 'task':
      // Prototype row meta `SRC[t.src]`: where it came from (the due date is the chip on the right).
      return i.origin_ref?.label ?? TASK_SOURCE_LABEL[i.source];
    case 'client_request':
      return `Via ${i.via}`;
    case 'internal_request':
      // Who asked and what kind of help; a prospect is named when the request is not about a client yet.
      return `${firstName(i.requester.name)}, ${INTERNAL_REQUEST_TYPE_LABEL[i.type].label.toLowerCase()}${i.prospect ? ` for ${i.prospect}` : ''}`;
    case 'client_task':
      // Prototype row meta: where it was said, e.g. "Meeting 28 Sep".
      return i.source ?? 'Client task';
    case 'approval':
      return `${firstName(i.change_request.by.name)} asks for a change`;
    case 'escalation':
      return 'Escalation';
    case 'proposed':
      return i.quote ?? 'Proposed';
  }
}

export function WorkRow({ item: i, onOpen }: { item: WorkItem; onOpen: () => void }) {
  const today = todayIso();
  const chips: ReactNode[] = [];
  if (i.kind === 'task' && !i.done && i.effective_head_priority === 'first')
    chips.push(
      <span key="first" className="rank first">
        Do first
      </span>,
    );
  for (const b of i.badges)
    chips.push(
      <span key={b.key + b.label} className={`chip ${b.tone === 'neutral' ? '' : b.tone}`} title={b.title}>
        {b.label}
      </span>,
    );
  // Step-3 rows: the prototype's extra chip and clock chip (reqClock / cmClock / severity).
  const step3 = i.kind === 'client_request' || i.kind === 'internal_request' || i.kind === 'client_task' || i.kind === 'escalation';
  if (i.kind === 'internal_request') chips.push(<span key="team" className="chip">{i.team === 'sales' ? 'Sales' : 'Marketing'}</span>);
  if (i.kind === 'client_task' && i.promised_to) chips.push(<span key="for" className="chip">For {i.promised_to}</span>);
  const clockChip = (key: string, c: { label: string; tone: string }) => chips.push(<span key={key} className={`chip ${c.tone}`}>{c.label}</span>);
  if (i.kind === 'client_request' && !i.badges.some((b) => b.key === 'past_response_time')) clockChip('clock', i.status === 'done' ? { label: 'Done', tone: 'hot' } : rowClock(i.hours_left));
  if (i.kind === 'internal_request') {
    if (i.status === 'done' || i.status === 'declined') clockChip('clock', { label: INTERNAL_REQUEST_STATUS_LABEL[i.status], tone: i.status === 'done' ? 'hot' : '' });
    else if (i.status === 'needs_details') clockChip('clock', { label: 'Waiting on the requester', tone: 'info' });
    else if (i.hours_to_ack != null) clockChip('clock', rowClock(i.hours_to_ack));
  }
  if (i.kind === 'client_task' && !i.badges.some((b) => b.key === 'overdue' || b.key === 'due_today')) clockChip('clock', clientTaskChip(i, today));
  if (i.kind === 'escalation' && i.status === 'closed') clockChip('clock', { label: i.closed_on ? `Closed ${shortDay(i.closed_on)}` : 'Closed', tone: 'hot' });
  else if (i.kind === 'escalation') clockChip('clock', { label: `${SEVERITY[i.severity].label}, since ${shortDay(i.opened_on)}`, tone: SEVERITY[i.severity].tone });
  // Every row says when it is due; the server adds the overdue/today chip, a later date reads "In 3 days".
  const hasDue = i.badges.some((b) => b.key === 'overdue' || b.key === 'due_today' || b.key === 'past_response_time');
  if (!step3 && !hasDue && i.due && !(i.kind === 'task' && i.done)) {
    const d = dueText(i.due, today);
    chips.push(
      <span key="due" className={`chip ${d.tone}`}>
        {daysBetween(today, i.due) > 14 ? dayLabel(i.due) : d.label}
      </span>,
    );
  }
  return (
    <div
      className="it"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen())}
    >
      <Own item={i} />
      <span className="kd">{KIND_LABEL[i.kind]}</span>
      <div className="grow" style={{ minWidth: 0 }}>
        <div className="tt">{i.title}</div>
        <div className="mm">{subLine(i)}</div>
      </div>
      <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>{chips}</span>
    </div>
  );
}

// ---------------------------------------------------------------- grouping

interface Group {
  key: string;
  label: string;
  prio: Priority | null;
  rows: WorkItem[];
  order: string;
}

type GroupOf = Omit<Group, 'rows'>;

/** The panel a row belongs to under the chosen grouping. */
function groupOf(i: WorkItem, by: GroupKey, today: string): GroupOf {
  if (by === 'client') {
    return i.client
      ? { key: i.client.id, label: i.client.name, order: `${PRIO_RANK[i.client.priority]}${i.client.name}`, prio: i.client.priority }
      : { key: '_none', label: 'Not about a client', order: '9', prio: null };
  }
  if (by === 'who') return i.assignee ? { key: i.assignee.id, label: i.assignee.name, order: `0${i.assignee.name}`, prio: null } : { key: '_none', label: 'Not assigned', order: '9', prio: null };
  if (by === 'due') {
    const d = i.due ? daysBetween(today, i.due) : null;
    const [k, l] =
      d === null ? ['5', 'No due date'] : d < 0 ? ['0', 'Overdue'] : d === 0 ? ['1', 'Today'] : d === 1 ? ['2', 'Tomorrow'] : d <= 7 ? ['3', 'This week'] : ['4', 'Later'];
    return { key: k, label: l, order: k, prio: null };
  }
  const t = TYPE_OF[i.kind] ?? 'task';
  const label: Record<string, string> = { escalation: 'Escalations', request: 'Requests', task: 'Tasks', approval: 'Approvals' };
  const order: Record<string, string> = { escalation: '0', request: '1', task: '2', approval: '3' };
  return { key: t, label: label[t] ?? KIND_LABEL[i.kind], order: order[t] ?? '4', prio: null };
}

function groupRows(items: WorkItem[], by: GroupKey): Group[] {
  const today = todayIso();
  const m = new Map<string, Group>();
  for (const i of items) {
    const o = groupOf(i, by, today);
    let g = m.get(o.key);
    if (!g) m.set(o.key, (g = { ...o, rows: [] }));
    g.rows.push(i);
  }
  return [...m.values()].sort((a, b) => a.order.localeCompare(b.order));
}

/** The kinds each By-type panel gathers (as groupOf files them). */
const TYPE_KINDS: Record<string, WorkKind[]> = {
  escalation: ['escalation'],
  request: ['client_request', 'internal_request'],
  task: ['task', 'client_task', 'proposed'],
  approval: ['approval'],
};

/**
 * The panels whose count is partial while "Show more" has more to load: they get "N+", as the type pills do.
 * GET /work pages in the grouping's order (group_by client|who|type; by due date, overdue then due date), so the
 * rows still to come all sort after the last row loaded. By client, person or due date a panel is one run of
 * that order, so only the panel holding the last row can be cut (panels draw by priority, so it need not be
 * drawn last). By type the server orders by row kind and a panel can gather several kinds (Requests, Tasks),
 * so a panel is cut when any of its kinds sorts at or after the last row's kind (byte order, as the server's
 * COLLATE "C").
 */
function cutGroups(last: WorkItem | undefined, by: GroupKey, more: boolean): Set<string> {
  if (!more || !last) return new Set();
  if (by === 'type') return new Set(Object.keys(TYPE_KINDS).filter((g) => TYPE_KINDS[g]!.some((k) => k >= last.kind)));
  return new Set([groupOf(last, by, todayIso()).key]);
}
